import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";

import { Column } from "@/components/Column";
import { LoadingIndicator } from "@/components/LoadingIndicator";
import { Row } from "@/components/Row";
import { FormEnumSelect } from "@/components/form/FormEnumSelect";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";

import { useGetCachedCompaniesSummary } from "@/hooks/sets/useGetCachedCompaniesSummary";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { JobStatus } from "@/types";

const formSchema = z.object({
  companyId: z.string().min(1, "Required"),
  jobId: z.string().min(1, "Required"),
});

type FormSchema = z.infer<typeof formSchema>;

export const AddToPipelineDialog = ({
  candidateName,
  currentCompanyId,
  currentJobId,
  isElsewhere,
  isPending,
  onConfirm,
  onOpenChange,
  open,
}: {
  candidateName: string;
  currentCompanyId: string;
  currentJobId: string;
  // When true, show a company/job picker (excluding the current job); otherwise add to the current job.
  isElsewhere: boolean;
  isPending: boolean;
  onConfirm: (jobId: string, companyId: string) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}) => {
  /* Database CRUD Operations */
  // Read
  const companies = useGetCachedCompaniesSummary();

  /* React Hooks */
  const form = useForm<FormSchema>({
    defaultValues: {
      companyId: isElsewhere ? "" : currentCompanyId,
      jobId: isElsewhere ? "" : currentJobId,
    },
    resolver: zodResolver(formSchema),
  });

  const selectedCompanyId = useWatch<FormSchema>({
    control: form.control,
    name: "companyId",
  });

  /* Helpers */
  const companiesWithJobs = companies.filter(({ jobs }) => jobs.length > 0);
  const selectedCompany = companiesWithJobs.find(
    ({ id }) => id === selectedCompanyId,
  );
  // Can't source a candidate into the job they're already being searched for, or a closed job.
  const jobs =
    selectedCompany?.jobs.filter(
      (job) => job.status !== JobStatus.CLOSED && job.id !== currentJobId,
    ) ?? [];

  const companyOptions = companiesWithJobs.map((company) => ({
    label: company.name,
    value: company.id,
  }));
  const jobOptions = jobs.map((job) => ({
    label: job.title,
    value: job.id,
  }));

  /* Handlers */
  const handleFormSubmit = (data: FormSchema) => {
    mixpanelUtils.trackFormSubmission(
      "Add to Pipeline",
      MIXPANEL_SCREENS.SEARCH_RESULT_SHEET,
    );
    onConfirm(data.jobId, data.companyId);
  };

  /* Render */
  const render = () => (
    <Dialog
      onOpenChange={(open) => {
        form.reset();
        onOpenChange(open);
      }}
      open={open}
    >
      <DialogContent className="flex flex-col gap-8">
        <DialogHeader className="flex flex-col gap-4">
          <DialogTitle className="text-2xl font-semibold text-slate-700">
            Add to pipeline
          </DialogTitle>
          <DialogDescription className="!m-0 text-base font-medium text-slate-700">
            {candidateName} will be added in Outreach. All communication is
            human-led — the AI scheduling agent will not reach out, so
            you&apos;ll need to contact them yourself.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            className="flex flex-col gap-6"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleFormSubmit)}
          >
            {isElsewhere && (
              <Column className="gap-4">
                <FormEnumSelect
                  fieldName="companyId"
                  form={form}
                  label="Select a company"
                  onChange={() => {
                    form.setValue("jobId", "");
                  }}
                  options={companyOptions}
                  placeholder="Select a company"
                />
                <FormEnumSelect
                  fieldName="jobId"
                  form={form}
                  label="Select a job"
                  options={jobOptions}
                  placeholder="Select a job"
                />
              </Column>
            )}
            <DialogFooter>
              <Button
                className="rounded-full"
                disabled={isPending}
                onClick={() => {
                  onOpenChange(false);
                }}
                type="button"
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                className="rounded-full"
                disabled={isPending}
                type="submit"
              >
                {isPending ? (
                  <Row className="items-center gap-2">
                    <LoadingIndicator gearColor="white" variant="small" />
                    Add to pipeline
                  </Row>
                ) : (
                  "Add to pipeline"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );

  return render();
};
